import {
  Box,
  Button,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ReactSelect from "react-select";
import Layout from "../../../components/dashboard/Layout";
import LogoutMenu from "../../../components/dashboard/Layout/logout-menu";
import { getLiveVideosRequest } from "../../../modules/admin-modules/liveVideos/Actions";
import { livevideoReportRequest } from "../../../modules/admin-modules/liveVideos/Actions";
import { centerHwRequest } from "../../../modules/admin-modules/reports/Actions";

const CAMERA_OPTIONS = [
  { value: "on", label: "On" },
  { value: "off", label: "Off" },
];

const CLASS_WORK_OPTIONS = [
  { value: "Excellent", label: "Excellent" },
  { value: "V.Good", label: "V.Good" },
  { value: "Good", label: "Good" },
  { value: "Fair", label: "Fair" },
];

function cameraLabel(camera) {
  if (camera === "on") return "On";
  if (camera === "off") return "Off";
  return "—";
}

function buildParentMessage({ subjectName, sessionTitle, studentName, camera, grade }) {
  const subj = subjectName?.trim() || "your subject";
  const session = sessionTitle?.trim() || "Live session";
  const student = studentName?.trim() || "Student";
  return [
    `Edutopia — ${subj}`,
    `Live session attendance update`,
    `Session: ${session}`,
    `Student: ${student}`,
    `Camera: ${cameraLabel(camera)}`,
    `Class work evaluation: ${grade || "—"}`,
    `Thank you.`,
  ].join("\n");
}

const focusRing = "0 0 0 1px rgba(122, 20, 34, 0.22)";

const selectStyles = {
  control: (styles, state) => ({
    ...styles,
    width: "100%",
    background: "#FFFFFF",
    borderColor: state.isFocused ? "#7A1422" : "rgba(214, 206, 192, 0.85)",
    borderRadius: "14px",
    minHeight: "44px",
    boxShadow: state.isFocused ? focusRing : "none",
    "&:hover": { borderColor: "#7A1422" },
  }),
  menu: (styles) => ({
    ...styles,
    borderRadius: "14px",
    overflow: "hidden",
    border: "1px solid rgba(214, 206, 192, 0.85)",
    boxShadow: "0 14px 40px rgba(139, 24, 40, 0.14)",
    zIndex: 20,
  }),
  menuList: (styles) => ({
    ...styles,
    padding: "4px",
    overflowX: "hidden",
    overflowY: "auto",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 1600 }),
  option: (styles, { isFocused, isSelected }) => ({
    ...styles,
    backgroundColor: isSelected
      ? "rgba(139, 24, 40, 0.12)"
      : isFocused
        ? "rgba(122, 20, 34, 0.06)"
        : "#FFFFFF",
    color: "#7A1422",
    fontWeight: isSelected ? 800 : 600,
    cursor: "pointer",
  }),
  singleValue: (styles) => ({ ...styles, color: "#7A1422", fontWeight: 800 }),
  placeholder: (styles) => ({
    ...styles,
    color: "rgba(122, 20, 34, 0.55)",
    fontWeight: 700,
  }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (styles, state) => ({
    ...styles,
    color: state.isFocused ? "#7A1422" : "rgba(122, 20, 34, 0.55)",
  }),
};

/** Compact row selects — matches admin cream / burgundy controls (no native blue ring). */
const inlineSelectStyles = {
  control: (styles, state) => ({
    ...styles,
    minHeight: "38px",
    minWidth: "120px",
    fontSize: "14px",
    backgroundColor: "#FFFFFF",
    borderColor: state.isFocused ? "#7A1422" : "rgba(214, 206, 192, 0.85)",
    borderRadius: "12px",
    borderWidth: "1px",
    boxShadow: state.isFocused ? focusRing : "none",
    cursor: state.isDisabled ? "not-allowed" : "pointer",
    opacity: state.isDisabled ? 0.55 : 1,
    "&:hover": { borderColor: state.isDisabled ? "rgba(214, 206, 192, 0.85)" : "#7A1422" },
  }),
  valueContainer: (styles) => ({ ...styles, padding: "0 8px" }),
  menu: (styles) => ({
    ...styles,
    borderRadius: "12px",
    overflow: "hidden",
    border: "1px solid rgba(214, 206, 192, 0.85)",
    boxShadow: "0 14px 40px rgba(139, 24, 40, 0.14)",
    zIndex: 20,
  }),
  menuPortal: (base) => ({ ...base, zIndex: 1600 }),
  menuList: (styles) => ({
    ...styles,
    padding: "4px",
    overflowX: "hidden",
    overflowY: "auto",
  }),
  option: (styles, { isFocused, isSelected }) => ({
    ...styles,
    borderRadius: "8px",
    margin: "2px 0",
    padding: "8px 10px",
    fontSize: "14px",
    backgroundColor: isSelected
      ? "rgba(139, 24, 40, 0.12)"
      : isFocused
        ? "rgba(122, 20, 34, 0.06)"
        : "transparent",
    color: "#7A1422",
    fontWeight: isSelected ? 800 : 600,
    cursor: "pointer",
  }),
  singleValue: (styles) => ({ ...styles, color: "#7A1422", fontWeight: 700 }),
  placeholder: (styles) => ({
    ...styles,
    color: "rgba(122, 20, 34, 0.5)",
    fontWeight: 600,
    fontSize: "13px",
  }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (styles, state) => ({
    ...styles,
    padding: "4px",
    color: state.isFocused ? "#7A1422" : "rgba(122, 20, 34, 0.5)",
  }),
};

const LiveVideoAttendanceReport = () => {
  const dispatch = useDispatch();
  const user = useSelector((s) => s.user.userData);
  const subjectName = user?.subject?.name || "";

  const liveVideos = useSelector((s) => s.adminLiveVideos.liveVideos);
  const liveVideosReport = useSelector((s) => s.adminLiveVideos.liveVideosReport);
  const liveListLoading = useSelector((s) => s.adminLiveVideos.isLoading);
  const reportSending = useSelector((s) => s.reports.isLoading);

  const [liveVideoId, setLiveVideoId] = useState("");
  const [rowState, setRowState] = useState({});

  useEffect(() => {
    dispatch(getLiveVideosRequest());
  }, [dispatch]);

  useEffect(() => {
    setRowState({});
    if (!liveVideoId) return;
    dispatch(livevideoReportRequest(liveVideoId));
  }, [liveVideoId, dispatch]);

  const liveOptions = useMemo(() => {
    const raw = Array.isArray(liveVideos) ? liveVideos : [];
    return raw.map((v) => ({
      value: String(v.id),
      label: v.title || `Live #${v.id}`,
    }));
  }, [liveVideos]);

  const selectedLive = useMemo(() => {
    const raw = Array.isArray(liveVideos) ? liveVideos : [];
    return raw.find((v) => String(v.id) === String(liveVideoId)) || null;
  }, [liveVideos, liveVideoId]);

  const sessionTitle = selectedLive?.title || "";

  const students = useMemo(() => {
    if (!liveVideosReport) return [];
    const list = liveVideosReport.students ?? liveVideosReport;
    return Array.isArray(list) ? list : [];
  }, [liveVideosReport]);

  const updateRowField = useCallback((studentId, field, value) => {
    setRowState((prev) => ({
      ...prev,
      [studentId]: { ...(prev[studentId] || {}), [field]: value },
    }));
  }, []);

  const sendRowToParent = useCallback(
    (studentId, studentName) => {
      const st = rowState[studentId] || {};
      if (!st.camera || !st.grade) return;
      const evaluation = buildParentMessage({
        subjectName,
        sessionTitle,
        studentName,
        camera: st.camera,
        grade: st.grade,
      });
      dispatch(
        centerHwRequest({
          student_id: studentId,
          evaluation,
        })
      );
    },
    [dispatch, subjectName, sessionTitle, rowState]
  );

  return (
    <Layout>
      <Box
        minH="400px"
        maxW="1440px"
        mx="auto"
        w="100%"
        px={{ base: 4, sm: 5, md: 8, lg: 10 }}
        pt={{ base: 6, md: 8 }}
        pb={{ base: 10, md: 12 }}
      >
        <Flex
          w="100%"
          mb={5}
          justifyContent="space-between"
          alignItems={{ base: "stretch", md: "flex-start" }}
          gap={4}
          flexWrap="wrap"
        >
          <Box flex="1" minW="0">
            <Text fontSize={{ base: "18px", md: "24px" }} fontWeight="900" color="#7A1422">
              Live video attendance report
            </Text>
            <Text mt={1} fontSize="sm" fontWeight="700" color="rgba(122, 20, 34, 0.55)">
              Select a live session, choose camera status and class-work evaluation for each student,
              then click Send to notify the parent (same channel as center homework notifications).
            </Text>
          </Box>
          <Flex align="flex-start" gap={3} flexWrap="wrap" justifyContent={{ base: "flex-start", md: "flex-end" }}>
            <Box minW={{ base: "100%", md: "320px" }} maxW={{ base: "100%", md: "420px" }}>
              <ReactSelect
                options={liveOptions}
                isLoading={liveListLoading}
                placeholder="Select live video"
                styles={selectStyles}
                value={liveOptions.find((o) => o.value === String(liveVideoId)) || null}
                onChange={(opt) => setLiveVideoId(opt?.value ? String(opt.value) : "")}
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                menuPosition="fixed"
              />
            </Box>
            <LogoutMenu />
          </Flex>
        </Flex>

        <Box
          border="1px solid rgba(214, 206, 192, 0.85)"
          bg="#FAF2EB"
          borderRadius="18px"
          p={{ base: 4, md: 6 }}
          boxShadow="0 18px 55px rgba(0,0,0,0.08)"
          overflowX="auto"
        >
          {!liveVideoId ? (
            <Text fontWeight="600" color="rgba(122, 20, 34, 0.55)">
              Choose a live video to load attendees.
            </Text>
          ) : liveListLoading && students.length === 0 ? (
            <Text fontWeight="600" color="#7A1422">
              Loading attendees…
            </Text>
          ) : students.length === 0 ? (
            <Text fontWeight="600" color="rgba(122, 20, 34, 0.55)">
              No students returned for this session.
            </Text>
          ) : (
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th color="#7A1422">#</Th>
                  <Th color="#7A1422">Student</Th>
                  <Th color="#7A1422">Code</Th>
                  <Th color="#7A1422">Phone</Th>
                  <Th color="#7A1422">Camera</Th>
                  <Th color="#7A1422">Class work</Th>
                  <Th color="#7A1422">Send</Th>
                </Tr>
              </Thead>
              <Tbody>
                {students.map((row, index) => {
                  const id = row.student?.id ?? row.id;
                  if (id == null) return null;
                  const st = rowState[id] || {};
                  const name = row.student?.name ?? row.name ?? "—";
                  const canSend = Boolean(st.camera && st.grade);
                  return (
                    <Tr key={id}>
                      <Td fontWeight="700">#{index + 1}</Td>
                      <Td fontWeight="600">{name}</Td>
                      <Td>{row.user_name || row.code || "—"}</Td>
                      <Td>{row.phone || "—"}</Td>
                      <Td minW="148px" verticalAlign="middle">
                        <ReactSelect
                          instanceId={`cam-${id}`}
                          inputId={`cam-${id}`}
                          options={CAMERA_OPTIONS}
                          styles={inlineSelectStyles}
                          placeholder="Camera"
                          isClearable
                          isSearchable={false}
                          isDisabled={reportSending}
                          value={CAMERA_OPTIONS.find((o) => o.value === st.camera) ?? null}
                          onChange={(opt) => updateRowField(id, "camera", opt?.value ?? "")}
                          menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                          menuPosition="fixed"
                        />
                      </Td>
                      <Td minW="168px" verticalAlign="middle">
                        <ReactSelect
                          instanceId={`grade-${id}`}
                          inputId={`grade-${id}`}
                          options={CLASS_WORK_OPTIONS}
                          styles={inlineSelectStyles}
                          placeholder="Evaluation"
                          isClearable
                          isSearchable={false}
                          isDisabled={reportSending}
                          value={CLASS_WORK_OPTIONS.find((o) => o.value === st.grade) ?? null}
                          onChange={(opt) => updateRowField(id, "grade", opt?.value ?? "")}
                          menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                          menuPosition="fixed"
                        />
                      </Td>
                      <Td>
                        <Button
                          size="sm"
                          borderRadius="lg"
                          bg="#8B1828"
                          color="white"
                          fontWeight="800"
                          isDisabled={!canSend || reportSending}
                          _hover={{ bg: "#7A1422" }}
                          onClick={() => sendRowToParent(id, name)}
                        >
                          Send
                        </Button>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          )}
        </Box>
      </Box>
    </Layout>
  );
};

export default LiveVideoAttendanceReport;
